import type { QualityFormulaType, SpecType } from '@local/lib';

import { AdjustmentMode } from '@local/lib';
import { Checkbox, Icon, Tooltip } from '@salesforce/design-system-react';

import type { ThresholdFormulaRow } from '../qualityFormulaTypes';

import { IconCategory } from '../../../shared/types/Enum';
import {
  ADJUSTMENT_MODE_COLOR,
  ADJUSTMENT_MODES,
  AMOUNT_CONFIG_BY_FORMULA_TYPE,
  getSpecUnit,
  rowUsesLowerBoundAsContractedSpec,
} from '../qualityFormulaMetadata';
import { validateRowAmount } from '../qualityFormulaValidation';
import { FormulaField } from './FormulaField';

interface ThresholdRowFieldsProps {
  contractedSpec: number;
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof ThresholdFormulaRow,
    value: boolean | number | string,
  ) => void;
  rows: ThresholdFormulaRow[];
  /** Lays both selected sides out in a row instead of stacking them — only where there's full card
   * width to spare (Contract Price/Deemed Price), not beside Price Components' narrower column. */
  sideBySide?: boolean;
  specType: SpecType;
}

interface ThresholdSideRowProps {
  contractedValue: number;
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRowChange: (field: keyof ThresholdFormulaRow, value: number | string) => void;
  row: ThresholdFormulaRow;
  specType: SpecType;
}

// One side (Discount or Premium) of a Threshold spec — its bound is entirely derived from the
// shared contracted value above, so this only ever exposes the rate fields to configure. Which
// side(s) are present is controlled entirely by the checkbox selector above, not from here.
const ThresholdSideRow: React.FC<ThresholdSideRowProps> = ({
  contractedValue,
  formulaType,
  onRowChange,
  row,
  specType,
}) => {
  const amountConfig = AMOUNT_CONFIG_BY_FORMULA_TYPE[formulaType];
  const errors = validateRowAmount(row, formulaType);
  // Which direction Actual has to be in comes from the spec's own higher-value effect (via
  // rowUsesLowerBoundAsContractedSpec) — never hardcoded per adjustmentMode, since e.g. Discount
  // is "above" for Moisture but "at or below" for a premium-effect spec like BTU.
  const appliesAboveContracted = rowUsesLowerBoundAsContractedSpec(row.adjustmentMode, specType);
  // The icon shows the *price* direction (Premium up, Discount down), not the Actual-value
  // direction above — those two can disagree (e.g. Moisture's Discount side is "Actual above" but
  // still a downward price move), so pairing an up-arrow with a red/negative row would read backwards.
  const directionIconName = row.adjustmentMode === AdjustmentMode.Premium ? 'arrowup' : 'arrowdown';
  const priceEffect = row.adjustmentMode === AdjustmentMode.Premium ? 'increases.' : 'decreases.';
  const sideColor = ADJUSTMENT_MODE_COLOR[row.adjustmentMode];
  const amountField = (
    <FormulaField
      error={errors.amount}
      fallback={0}
      label={amountConfig.label}
      onChange={value => {
        onRowChange(amountConfig.field, value);
      }}
      tooltip={amountConfig.hint}
      value={row[amountConfig.field]}
    />
  );
  const perIncrementField = (
    <FormulaField
      error={errors.perIncrement}
      fallback={0}
      label={`Per increment (${getSpecUnit(specType)})`}
      onChange={value => {
        onRowChange('perIncrement', value);
      }}
      tooltip={`Price applies per this many ${getSpecUnit(specType)} of difference (usually 1)`}
      value={row.perIncrement}
    />
  );

  return (
    <div className="slds-box slds-box_x-small slds-theme_default" style={{ borderLeft: `3px solid ${sideColor}` }}>
      <div
        className="slds-grid slds-grid_vertical-align-center slds-p-bottom_xx-small"
        style={{ gap: 'var(--slds-g-spacing-2, 0.5rem)' }}
      >
        <Icon
          assistiveText={{ label: row.adjustmentMode === AdjustmentMode.Premium ? 'Premium' : 'Discount' }}
          category={IconCategory.Utility}
          name={directionIconName}
          size="xx-small"
          style={{ fill: sideColor }}
        />
        <span className="slds-text-body_small">
          If Actual is {appliesAboveContracted ? 'above' : 'at or below'} {contractedValue}, price {priceEffect}
        </span>
      </div>
      <div className="slds-grid slds-wrap slds-gutters_small">
        <div className="slds-col slds-size_1-of-2">{amountField}</div>
        <div className="slds-col slds-size_1-of-2">{perIncrementField}</div>
      </div>
    </div>
  );
};

interface ThresholdAppliesToControlProps {
  onSideToggle: (specType: SpecType, adjustmentMode: AdjustmentMode, isVisible: boolean) => void;
  rows: ThresholdFormulaRow[];
  specType: SpecType;
}

// "Applies to" shows a checkmark (this side currently applies), not `ADJUSTMENT_MODES`' own
// direction-arrow labels (↓/↑, used everywhere a side's *price effect* matters — Adjustment
// Behavior, the Range table) — see the Ash/Sulfur references, where the same two sides read
// "✓ Discount"/"✓ Premium" here specifically. Keyed off `AdjustmentMode` (not re-declared
// alongside `ADJUSTMENT_MODES`) so both still share one source of truth for which sides exist.
const APPLIES_TO_LABEL: Record<AdjustmentMode, string> = {
  [AdjustmentMode.Discount]: '✓ Discount',
  [AdjustmentMode.Premium]: '✓ Premium',
};

// Button-group checkboxes are independent: both sides can be active at once.
export const ThresholdAppliesToControl: React.FC<ThresholdAppliesToControlProps> = ({
  onSideToggle,
  rows,
  specType,
}) => {
  const selectedSides = rows.map(row => row.adjustmentMode);

  return (
    <div className="qfw-applies-to-control">
      {ADJUSTMENT_MODES.map(mode => {
        const isChecked = selectedSides.includes(mode.value);
        // The sole remaining selected side can't be unchecked — "at least one side" is enforced
        // by disabling its own checkbox (with an explanatory tooltip) rather than silently
        // ignoring the click.
        const isOnlySide = isChecked && selectedSides.length === 1;

        const checkbox = (
          <Checkbox
            checked={isChecked}
            disabled={isOnlySide}
            key={mode.value}
            labels={{ label: APPLIES_TO_LABEL[mode.value] }}
            onChange={(_event, data) => {
              onSideToggle(specType, mode.value, data.checked);
            }}
            variant="button-group"
          />
        );

        const sideClassName = mode.value === AdjustmentMode.Discount ? 'qfw-side-discount' : 'qfw-side-premium';

        return (
          <div className={`slds-checkbox_button-group ${sideClassName}`} key={mode.value}>
            {isOnlySide ? (
              <Tooltip align="top" content="At least one side is required">
                <span tabIndex={-1}>{checkbox}</span>
              </Tooltip>
            ) : (
              checkbox
            )}
          </div>
        );
      })}
    </div>
  );
};

// Renders THRESHOLD tier mode's "Adjustment Behavior": one compact side card per selected side.
// The shared contracted value lives in the parent card's top row (see QualitySpecCard) since it
// applies to Range mode too. Bounds are never entered directly here — they're derived from that
// shared value. Which side(s) are present is controlled by ThresholdAppliesToControl, not here.
export const ThresholdRowFields: React.FC<ThresholdRowFieldsProps> = ({
  contractedSpec,
  formulaType,
  onRowChange,
  rows,
  sideBySide,
  specType,
}) => {
  // Discount always renders before Premium regardless of insertion order, matching how the two
  // sides read left-to-right in the legacy system this playground mirrors.
  const orderedRows = [...rows].sort(
    (a, b) =>
      (a.adjustmentMode === AdjustmentMode.Discount ? -1 : 1) - (b.adjustmentMode === AdjustmentMode.Discount ? -1 : 1),
  );
  const sideRows = orderedRows.map(row => {
    const rowIndex = rows.indexOf(row);
    return (
      <div
        className={
          sideBySide
            ? `slds-col slds-size_1-of-1${orderedRows.length > 1 ? ' slds-medium-size_1-of-2' : ''}`
            : undefined
        }
        key={row.id}
      >
        <ThresholdSideRow
          contractedValue={contractedSpec}
          formulaType={formulaType}
          onRowChange={(field, value) => {
            onRowChange(specType, rowIndex, field, value);
          }}
          row={row}
          specType={specType}
        />
      </div>
    );
  });

  if (sideBySide) {
    return <div className="slds-grid slds-wrap slds-gutters_small">{sideRows}</div>;
  }

  // Plain `gap` (not `slds-gutters_x-small`) — that utility only ever applies padding to
  // `.slds-col` children to compensate for its own negative margin (see its own scss source);
  // these stacked rows aren't `.slds-col`s, so the class was only pulling this grid's left/right
  // edges in by its own negative margin with nothing to counteract it on either side, throwing
  // off the two side cards' own left/right insets relative to this panel's padding — and it never
  // provided any vertical spacing between stacked rows in the first place (it's a horizontal-only
  // utility), which is exactly why the two cards otherwise touch with no gap between them.
  return (
    <div className="slds-grid slds-grid_vertical slds-wrap" style={{ gap: 'var(--slds-g-spacing-3, 0.75rem)' }}>
      {sideRows}
    </div>
  );
};
